import React, { useEffect, useState } from 'react';
import { Button, TextField, DropdownSelect, FieldController, RequiredValidator } from '@keplerco/core';
import classNames from 'classnames';
import layoutStyles from '../../../../design/layout.module.css';
import { useForm } from 'react-hook-form';
import { useAppActions, useAppState } from '../../../../overmind';
import { PagePath } from '../../../../navigation/navigation.enums';
import { FetchType } from '../../../../enums';
import { EntitiesCardWidget } from '../../../../components/cards/entities-card.widget';
import { companySectors, companySizes } from '../freemium-data';

// Props for the company info page
interface CompanyInfoPageProps {
  onNext: () => void;
}

/**
 * Company Info onboarding step
 * Collects basic company information
 */

interface IFormValues {
  companyName: string;
  industrySector: string;
  companySize: string;
  website: string;
}

export function CompanyInfoPage({ onNext }: CompanyInfoPageProps): JSX.Element {
  const actions = useAppActions();
  const { freemiumAccount } = useAppState();
  // State for form fields
  const [websites, setWebsites] = useState<string[]>([]);
  const [newWebsite, setNewWebsite] = useState<string>('');

  const { handleSubmit, control, setValue, watch, reset } = useForm<any>({
    mode: 'onBlur'
  });

  useEffect(() => {
    reset({
      companyName: freemiumAccount?.companyName || '',
      industrySector: freemiumAccount?.industrySector || '',
      companySize: freemiumAccount?.companySize || '',
    });
    // Also update the websites array
    setWebsites(freemiumAccount?.websiteUrls || []);
  }, [freemiumAccount, reset]);

  // Form validation
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Handle adding a new website
  const handleAddWebsite = () => {
    const websiteValue = watch('website');
    if (!websiteValue) return;

    // Basic URL validation
    if (!websiteValue.match(/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/)) {
      setErrors({ ...errors, website: 'Please enter a valid website URL' });
      return;
    }

    setErrors({ ...errors, website: '' });
    const websiteWithProtocol = websiteValue.startsWith('http') ? websiteValue : `https://${websiteValue}`;

    setWebsites([...websites, websiteWithProtocol]);
    setValue('website', '');
    setNewWebsite('');
  };

  // Handle removing a website
  const handleRemoveWebsite = (url: string) => {
    setWebsites(websites.filter(website => website !== url));
  };

  // Handle form submission
  const handleContinue = async (formValues: IFormValues) => {
    // Validate form
    const newErrors: { [key: string]: string } = {};

    if (!formValues.companyName) {
      newErrors.companyName = 'Company name is required';
    }

    if (!formValues.industrySector) {
      newErrors.sector = 'Please select a sector';
    }

    if (!formValues.companySize) {
      newErrors.size = 'Please select a company size';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    // Save data to state
    actions.startLoader({ path: PagePath.onboarding, type: FetchType.Sending });
    await actions.updateCompanyInformation({
      companyName: formValues.companyName,
      industrySector: formValues.industrySector,
      companySize: formValues.companySize,
      websiteUrls: websites
    });
    await actions.getFreemiumAccount();
    actions.stopLoader(PagePath.onboarding);
    // Go to next step
    onNext();
  };

  return (
    <form style={{ display: 'flex', flexDirection: 'column', gap: 24, width: '100%' }} onSubmit={handleSubmit(handleContinue)}>
      <EntitiesCardWidget title="Company Information" description="This information helps us customize your skills and roles taxonomy to best fit your organization.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <FieldController
              name="companyName"
              control={control}
              rules={new RequiredValidator('Company name is required')}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  placeholder=""
                  type="text"
                  label="Company Name"
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    invalid: !!fieldState.error,
                    message: fieldState.error?.message ?? 'Company name is required'
                  }}
                  responsive
                />
              )}
            />
          </div>

          <div>
            <label className="input-label">Websites</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <FieldController
                name="website"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    placeholder="https://example.com"
                    type="url"
                    label="" // Empty label to satisfy TypeScript
                    onChange={(e) => {
                      field.onChange(e);
                      setNewWebsite(e.target.value);
                    }}
                    responsive
                    validation={{
                      dirty: fieldState.isDirty || !!errors.website,
                      invalid: !!errors.website,
                      message: errors.website || ''
                    }}
                  />
                )}
              />
              <div style={{ width: '150px' }}>
                <button className={'button'} disabled={!newWebsite} type="button" onClick={handleAddWebsite} >
                  Add
                </button>
              </div>
            </div>
            {websites.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {websites.map((website) => (
                  <div key={website} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', padding: '8px 12px', backgroundColor: 'var(--background)', borderRadius: '4px' }}>
                    <div style={{ flex: 1 }}>{website}</div>
                    <div style={{ width: '132px' }}>
                      <button className={'button'} type="button" onClick={() => handleRemoveWebsite(website)} style={{ flexShrink: 0 }}>
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <FieldController
              name="industrySector"
              control={control}
              rules={new RequiredValidator('Please select a sector')}
              render={({ field, fieldState }) => (
                <DropdownSelect
                  {...field}
                  label="Industry Sector"
                  options={companySectors.map(sector => ({
                    label: sector.name,
                    value: sector.name
                  }))}
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    invalid: !!fieldState.error,
                    message: fieldState.error?.message ?? 'Please select a sector'
                  }}
                  responsive
                />
              )}
            />
          </div>
          <div>
            <FieldController
              name="companySize"
              control={control}
              rules={new RequiredValidator('Please select a company size')}
              render={({ field, fieldState }) => (
                <DropdownSelect
                  {...field}
                  label="Company Size"
                  options={companySizes.map(size => ({
                    label: size.range,
                    value: size.range
                  }))}
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    invalid: !!fieldState.error,
                    message: fieldState.error?.message ?? 'Please select a company size'
                  }}
                  responsive
                />
              )}
            />
          </div>
        </div>
      </EntitiesCardWidget>

      <footer className={classNames('card', layoutStyles.wizardFooter)} style={{ justifyContent: 'flex-end' }}>
        <Button type="button" filled>
          Next
        </Button>
      </footer>
    </form>
  );
}
